import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ProductCard from "@/components/feature/ProductCard";
import { categories, products } from "@/mocks/products";

const audienceTabs = [
  { v: "all", l: "All" },
  { v: "men", l: "Men" },
  { v: "women", l: "Women" },
];

type CatItem = { slug: string; name: string };

function CategoryFilterRow({
  items,
  activeSlug,
  onSelect,
}: {
  items: CatItem[];
  activeSlug: string;
  onSelect: (slug: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const compute = () => {
      const containerWidth = container.clientWidth;
      const gap = 6; // gap-x-1.5
      const moreBtnWidth = 56; // reserve for "More"
      const pills = Array.from(measure.querySelectorAll<HTMLElement>("[data-pill]"));
      let used = 0;
      let count = 0;
      for (let i = 0; i < pills.length; i += 1) {
        const w = pills[i].getBoundingClientRect().width;
        const next = used + (i === 0 ? 0 : gap) + w;
        const reserve = i === pills.length - 1 ? 0 : gap + moreBtnWidth;
        if (next + reserve <= containerWidth) {
          used = next;
          count = i + 1;
        } else {
          break;
        }
      }
      if (count === 0) count = 1;
      setVisibleCount(count);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [items]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);
  const overflowHasActive = overflowItems.some((it) => it.slug === activeSlug);

  const pillClass = (isActive: boolean) =>
    `nv-nowrap text-[11px] md:text-[13px] px-3 md:px-4 min-h-[30px] md:min-h-[40px] rounded-full border transition cursor-pointer inline-flex items-center ${
      isActive
        ? "bg-foreground-50 text-background-50 border-foreground-50"
        : "border-background-200/70 text-foreground-200 hover:text-foreground-50 hover:border-foreground-300"
    }`;

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      {/* Hidden measurer — full list, actual visual size */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="absolute -z-10 opacity-0 pointer-events-none top-0 left-0 flex items-center gap-x-1.5 whitespace-nowrap"
      >
        {items.map((c) => (
          <span key={c.slug} data-pill className={pillClass(false)}>
            {c.name}
          </span>
        ))}
      </div>

      {/* Visible single-row row */}
      <div className="flex items-center gap-x-1.5 lg:gap-2 flex-nowrap overflow-hidden">
        {visibleItems.map((c) => (
          <button
            key={c.slug}
            onClick={() => onSelect(c.slug)}
            className={pillClass(activeSlug === c.slug)}
          >
            {c.name}
          </button>
        ))}

        {overflowItems.length > 0 && (
          <div ref={menuRef} className="relative shrink-0">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className={pillClass(overflowHasActive) + " gap-1"}
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              More
              <i className={`ri-arrow-down-s-line transition ${menuOpen ? "rotate-180" : ""}`}></i>
            </button>
            {menuOpen && (
              <div className="absolute left-0 md:right-0 md:left-auto top-full mt-2 z-20 min-w-[160px] rounded-xl border border-background-200/70 bg-background-100 py-1.5 shadow-lg">
                {overflowItems.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => {
                      onSelect(c.slug);
                      setMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-[12px] md:text-[13px] transition cursor-pointer nv-nowrap ${
                      activeSlug === c.slug
                        ? "text-foreground-50 bg-background-200/40"
                        : "text-foreground-200 hover:text-foreground-50 hover:bg-background-200/40"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const audience = params.get("audience") || "all";
  const collection = params.get("collection") || "all";
  const filtered = useMemo(() => {
    let list = [...products];

    // Audience filter — MEN=men+unisex, WOMEN=women+unisex, ALL=everything
    if (audience === "men") {
      list = list.filter((p) => p.audience === "men" || p.audience === "unisex");
    } else if (audience === "women") {
      list = list.filter((p) => p.audience === "women" || p.audience === "unisex");
    }

    if (activeCat !== "all") list = list.filter((p) => p.categorySlug === activeCat);
    if (collection === "new") list = list.slice(0, 4);

    // Default order: footwear pinned to the first row, everything else keeps original order
    list.sort((a, b) => {
      const af = a.categorySlug === "footwear" ? 0 : 1;
      const bf = b.categorySlug === "footwear" ? 0 : 1;
      return af - bf;
    });
    return list;
  }, [activeCat, audience, collection]);

  const setCat = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("cat"); else next.set("cat", slug);
    setParams(next, { replace: true });
  };

  const setAudience = (a: string) => {
    const next = new URLSearchParams(params);
    if (a === "all") next.delete("audience"); else next.set("audience", a);
    setParams(next, { replace: true });
  };

  const toggleNewArrivals = () => {
    const next = new URLSearchParams(params);
    if (collection === "new") next.delete("collection"); else next.set("collection", "new");
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="solid" />
      <main className="flex-1 pt-[72px] md:pt-24">
        <section className="w-full px-5 md:px-10 pt-6 md:pt-10 pb-5 md:pb-6">
          <p className="text-[11px] md:text-[10px] tracking-[0.22em] uppercase text-foreground-400 mb-5 md:mb-3">Shop — AW26</p>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <h1 className="text-[30px] md:text-[38px] leading-[1.1] md:leading-[1.08] tracking-[-0.01em] text-foreground-50 font-light max-w-[340px] md:max-w-2xl">
              Field-tested essentials, quietly organized.
            </h1>
            <p className="text-[14px] md:text-[12px] leading-[1.3] text-foreground-400 mt-6 md:mt-0">
              {filtered.length} {filtered.length === 1 ? "product" : "products"}
            </p>
          </div>
        </section>

        <section className="w-full px-5 md:px-10 py-5 md:py-6 border-y border-background-200/50">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-8">
            {/* Category filters — single row with overflow dropdown */}
            <CategoryFilterRow
              items={[{ slug: "all", name: "All" }, ...categories]}
              activeSlug={activeCat}
              onSelect={setCat}
            />

            <div className="lg:ml-auto flex items-center gap-1.5 md:gap-3 flex-wrap">
              {/* Audience selector — ALL / MEN / WOMEN only */}
              <div className="flex items-center gap-1 p-0.5 md:p-1 rounded-full border border-background-200/70 shrink-0">
                {audienceTabs.map((c) => (
                  <button
                    key={c.v}
                    onClick={() => setAudience(c.v)}
                    className={`nv-nowrap text-[11px] md:text-[14px] px-2.5 md:px-4 min-h-[26px] md:min-h-[36px] rounded-full transition cursor-pointer inline-flex items-center ${
                      audience === c.v
                        ? "bg-foreground-50 text-background-50"
                        : "text-foreground-300 hover:text-foreground-50"
                    }`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>

              {/* New Arrivals — separate collection filter */}
              <button
                onClick={toggleNewArrivals}
                className={`nv-nowrap text-[11px] md:text-[13px] px-3 md:px-4 min-h-[30px] md:min-h-[40px] rounded-full border transition cursor-pointer inline-flex items-center shrink-0 ${
                  collection === "new"
                    ? "bg-foreground-50 text-background-50 border-foreground-50"
                    : "border-background-200/70 text-foreground-300 hover:text-foreground-50 hover:border-foreground-300"
                }`}
              >
                New Arrivals
              </button>

            </div>
          </div>
        </section>

        <section className="bg-white text-neutral-900 py-12 md:py-16">
          <div className="w-full px-5 md:px-10">
            {filtered.length === 0 ? (
              <div className="text-center py-20 text-[13px] text-neutral-500">
                No products match this filter yet.
                <div className="mt-4">
                  <Link to="/shop" className="border-b border-neutral-400 pb-1 hover:text-primary-600 cursor-pointer">Reset filters</Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-10 gap-y-8 md:gap-y-12 max-w-[1600px] mx-auto" data-product-shop>
                {filtered.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}